import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw, CheckCircle2, Wifi } from "lucide-react";
import { Button } from "@/components/ui/button";
import { realtimeBus } from "@/lib/realtime-bus";
import { cn } from "@/lib/utils";

const TABLE_LABELS: Record<string, string> = {
  children: "الأطفال",
  classes: "الفصول",
  staff: "الموظفين",
  parents: "أولياء الأمور",
  attendance: "الحضور",
  invoices: "الفواتير",
  payments: "المدفوعات",
  expenses: "المصاريف",
  fee_templates: "قوالب الرسوم",
  lessons: "الدروس",
  assignments: "الواجبات",
  exams: "الاختبارات",
  exam_submissions: "إجابات الاختبارات",
  activities: "الأنشطة",
  live_sessions: "الجلسات المباشرة",
  observations: "الملاحظات",
  leave_requests: "طلبات الإجازة",
  messages: "الرسائل",
  subscriptions: "الاشتراكات",
  nursery_modules: "الوحدات",
  nurseries: "الحضانات",
  plan_prices: "أسعار الباقات",
  subscription_invoices: "فواتير الاشتراكات",
  branches: "الفروع",
  financial_ledger: "السجلات المالية",
  salary_payments: "الرواتب",
  game_results: "نتائج الألعاب",
  child_game_results: "نتائج ألعاب الأطفال",
  notifications: "الإشعارات",
};

const RealtimeStatusBadge = () => {
  const queryClient = useQueryClient();
  const [lastUpdate, setLastUpdate] = useState<{ table: string; at: number } | null>(null);
  const [showFlash, setShowFlash] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const unsub = realtimeBus.subscribe((table) => {
      setLastUpdate({ table, at: Date.now() });
      setShowFlash(true);
      const t = setTimeout(() => setShowFlash(false), 2500);
      return () => clearTimeout(t);
    });
    return () => { unsub(); };
  }, []);

  // Force re-render every 30s to refresh "minutes ago" label
  const [, force] = useState(0);
  useEffect(() => {
    const i = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(i);
  }, []);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await queryClient.invalidateQueries();
    setTimeout(() => setRefreshing(false), 600);
  };

  const ago = lastUpdate ? Math.floor((Date.now() - lastUpdate.at) / 1000) : null;
  const agoLabel =
    ago === null
      ? "متصل"
      : ago < 5
      ? "تم التحديث الآن"
      : ago < 60
      ? `قبل ${ago} ث`
      : ago < 3600
      ? `قبل ${Math.floor(ago / 60)} د`
      : `قبل ${Math.floor(ago / 3600)} س`;

  const tableLabel = lastUpdate ? TABLE_LABELS[lastUpdate.table] || lastUpdate.table : null;

  return (
    <div className="flex items-center gap-1.5">
      {/* Live indicator */}
      <div
        className={cn(
          "hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border transition-all",
          showFlash
            ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
            : "bg-muted/50 text-muted-foreground border-border"
        )}
        title={tableLabel ? `آخر تحديث: ${tableLabel} — ${agoLabel}` : "متصل بالبث المباشر"}
      >
        {showFlash ? (
          <>
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>تم تحديث {tableLabel}</span>
          </>
        ) : (
          <>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <Wifi className="w-3 h-3" />
            <span>{agoLabel}</span>
          </>
        )}
      </div>

      {/* Manual refresh button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleManualRefresh}
        disabled={refreshing}
        className="h-8 w-8 text-muted-foreground hover:text-primary"
        title="تحديث البيانات يدوياً"
      >
        <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
      </Button>
    </div>
  );
};

export default RealtimeStatusBadge;
